import { computed, getCurrentScope, onScopeDispose, ref, } from "vue";
import { onBeforeRouteLeave, } from "vue-router";
import { useI18n } from "vue-i18n";
import { logger } from "@/infrastructure/services/LoggerService";
import { useAppToast } from "@/shared/composables/useAppToast";
/**
 * Page-level save orchestrator + unsaved-changes guard (OP-5555).
 *
 * Each editable bloc on the page registers itself with `{ id, isDirty, save }`.
 * The orchestrator aggregates `isDirty` across blocs, owns the Vue Router
 * `onBeforeRouteLeave` guard + `beforeunload` listener (so blocs don't each
 * install their own), and fans out `saveAll()` in parallel via
 * `Promise.allSettled`.
 *
 * Toast policy:
 *  - All blocs saved → one success toast.
 *  - Any bloc failed → one error toast per failing bloc (i18n key keyed by
 *    `blocErrorToastKey(id)`); no success toast.
 *
 * Failing blocs stay dirty because they never call their own `markAsSaved()`
 * — the orchestrator never touches their state directly.
 */
export function useSaveOrchestrator(options) {
    const { successToastKey, blocErrorToastKey = (id) => `${id}.saveError`, onNavigationBlocked, enableBeforeUnload = true, } = options;
    const { t } = useI18n();
    const { toastSuccess, toastError } = useAppToast();
    // Plain Map intentionally — wrapping it in `ref` / `reactive` would make
    // Vue deep-proxy each stored bloc and auto-unwrap the inner `isDirty` ref
    // on property access (`bloc.isDirty.value` would return `undefined`).
    // Reactivity of the aggregated `isDirty` is driven by `blocsVersion`
    // (bumped on register/unregister) + the inner per-bloc `isDirty` refs.
    const blocs = new Map();
    const blocsVersion = ref(0);
    const isSaving = ref(false);
    const isDirty = computed(() => {
        // Touch the version so re-registration retriggers the computation.
        void blocsVersion.value;
        for (const bloc of blocs.values()) {
            if (bloc.isDirty.value)
                return true;
        }
        return false;
    });
    function register(bloc) {
        if (blocs.has(bloc.id)) {
            // A duplicate id is always a bug — silently overwriting it would make
            // the page lose track of one of the blocs, surface no error, and the
            // "save fan-out reaches every bloc" guarantee would silently break.
            logger.error(`[useSaveOrchestrator] Duplicate bloc id "${bloc.id}" — the previous registration will be overwritten.`);
        }
        blocs.set(bloc.id, bloc);
        blocsVersion.value++;
        // Auto-unregister when the caller's setup scope disposes (component unmount).
        if (getCurrentScope()) {
            onScopeDispose(() => {
                if (blocs.delete(bloc.id))
                    blocsVersion.value++;
            });
        }
    }
    function unregister(id) {
        if (blocs.delete(id))
            blocsVersion.value++;
    }
    async function saveAll() {
        if (isSaving.value)
            return;
        const dirty = Array.from(blocs.values()).filter((b) => b.isDirty.value);
        if (dirty.length === 0)
            return;
        isSaving.value = true;
        try {
            const results = await Promise.allSettled(dirty.map((b) => b.save()));
            let anyFailed = false;
            results.forEach((result, index) => {
                if (result.status !== "rejected")
                    return;
                anyFailed = true;
                // Per-bloc error toast — never leak raw server text. The error is
                // logged with the bloc id so devs can correlate; the user sees the
                // translated key.
                const bloc = dirty[index];
                logger.error(`[useSaveOrchestrator] save() rejected for bloc "${bloc.id}":`, result.reason);
                toastError(blocErrorToastKey(bloc.id));
            });
            if (!anyFailed)
                toastSuccess(successToastKey);
        }
        finally {
            isSaving.value = false;
        }
    }
    // ── Navigation guard ─────────────────────────────────────────────────────
    const pendingNavigation = ref(null);
    const hasPendingNavigation = computed(() => pendingNavigation.value !== null);
    function proceedNavigation() {
        if (!pendingNavigation.value)
            return;
        const { next } = pendingNavigation.value;
        pendingNavigation.value = null;
        next();
    }
    function cancelNavigation() {
        if (!pendingNavigation.value)
            return;
        const { next } = pendingNavigation.value;
        pendingNavigation.value = null;
        next(false);
    }
    onBeforeRouteLeave((to, _from, next) => {
        if (isDirty.value && onNavigationBlocked) {
            pendingNavigation.value = { to, next };
            onNavigationBlocked(proceedNavigation, cancelNavigation);
        }
        else {
            next();
        }
    });
    // beforeunload: native browser refresh / tab-close prompt. The orchestrator
    // owns the single listener so blocs don't each register their own.
    function handleBeforeUnload(event) {
        if (!isDirty.value)
            return;
        event.preventDefault();
        const message = t("unsavedChanges.beforeUnloadMessage");
        event.returnValue = message;
        return message;
    }
    if (enableBeforeUnload && typeof window !== "undefined") {
        window.addEventListener("beforeunload", handleBeforeUnload);
        if (getCurrentScope()) {
            onScopeDispose(() => {
                window.removeEventListener("beforeunload", handleBeforeUnload);
            });
        }
    }
    return {
        isDirty,
        isSaving,
        register,
        unregister,
        saveAll,
        proceedNavigation,
        cancelNavigation,
        hasPendingNavigation,
    };
}
